import React from 'react';
import { Box, Typography, Divider, Container, Grid, Paper } from '@mui/material';
import founderImage from '../images/clients/founder.jpeg';
import SEO from '../components/SEO';

function About() {
  return (
    <Box sx={{ backgroundColor: '#fff', pt: { xs: 14, md: 18 }, pb: 8 }}>
      <SEO 
        title="About Us"
        description="Learn more about Fem Tech Solution, our mission to improve menstrual hygiene, and our founder's vision for sustainable real-world impact across India."
        keywords="about fem tech solution, menstrual hygiene, CSR projects India, sustainable pads, mr prithiv"
      />
      <Container maxWidth="lg">
        {/* Simple Box Header */}
        <Box sx={{ borderLeft: '5px solid #003366', pl: 3, mb: 6 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#333',
              textAlign: 'left',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}
          >
            ABOUT US
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#666',
              fontStyle: 'italic',
              fontWeight: 400,
              mt: 1
            }}
          >
            Improving menstrual hygiene with technology & care.
          </Typography>
        </Box>

        <Grid container spacing={6}>
          <Grid item xs={12} md={8}>
            <Typography sx={{ fontSize: '16px', lineHeight: 1.8, color: '#555', mb: 3 }}>
              Fem Tech Solution is a purpose-driven organization committed to improving menstrual hygiene and overall women’s well-being through innovative technology solutions.
            </Typography>
            <Typography sx={{ fontSize: '16px', lineHeight: 1.8, color: '#555', mb: 3 }}>
              We focus on providing reliable, safe, and accessible menstrual hygiene infrastructure across schools, colleges, workplaces, hospitals, and public facilities.
            </Typography>
            <Typography sx={{ fontSize: '16px', lineHeight: 1.8, color: '#555', mb: 3 }}>
              Founded with a mission to bridge the gap between awareness and real-world impact, we design, supply, and implement sanitary napkin vending machines and eco-friendly disposal systems.
            </Typography>

            <Box sx={{ backgroundColor: '#f9f9f9', p: 3, border: '1px solid #eee', mb: 3 }}>
              <Typography variant="h6" sx={{ color: '#003366', fontWeight: 'bold', mb: 2 }}>Complete Project Execution</Typography>
              <Typography sx={{ fontSize: '15px', color: '#666', mb: 2 }}>
                Our approach goes beyond product delivery. We partner with CSR companies, NGOs, government bodies, and institutions to ensure:
              </Typography>
              <ul style={{ color: '#555', paddingLeft: '20px' }}>
                <li>Site assessment</li>
                <li>Installation</li>
                <li>Training</li>
                <li>Maintenance</li>
                <li>Impact reporting</li>
              </ul>
              <Typography sx={{ fontSize: '15px', color: '#666', mt: 2 }}>
                By combining technology with social responsibility, Fem Tech Solution ensures every installation is ethical, scalable, and sustainable—delivering long-term value for both beneficiaries and partners.
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Paper id="vision" sx={{ p: 3, backgroundColor: '#f4f4f4', color: '#333', borderRadius: 0, borderTop: '4px solid #cc0000', scrollMarginTop: '100px' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#003366' }}>OUR VISION</Typography>
                <Typography sx={{ lineHeight: 1.6 }}>
                  To create a society where every woman and girl has safe, dignified, and uninterrupted access to menstrual hygiene solutions, regardless of economic or social background.
                </Typography>
              </Paper>

              <Paper id="mission" sx={{ p: 3, backgroundColor: '#f4f4f4', color: '#333', borderRadius: 0, borderTop: '4px solid #cc0000', scrollMarginTop: '100px' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#003366' }}>OUR MISSION</Typography>
                <ul style={{ paddingLeft: '20px', margin: 0, lineHeight: 1.6 }}>
                  <li style={{ marginBottom: '10px' }}>Provide reliable and cost-effective fem tech solutions for schools, colleges, workplaces, and public institutions</li>
                  <li style={{ marginBottom: '10px' }}>Partner with CSR companies, NGOs, and government bodies for large-scale implementation</li>
                  <li style={{ marginBottom: '10px' }}>Promote awareness, education, and sustainable hygiene practices</li>
                  <li>Deliver measurable social impact through structured projects and reporting</li>
                </ul>
              </Paper>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 8 }} />

        {/* Founder Section */}
        <Box id="founder" sx={{ border: '1px solid #ddd', p: { xs: 3, md: 5 }, backgroundColor: '#fff', scrollMarginTop: '100px' }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={3} sx={{ textAlign: 'center', borderRight: { md: '1px solid #eee' } }}>
              <Box
                component="img"
                src={founderImage}
                alt="Mr. Prithiv, Founder, Fem Tech Solution"
                sx={{
                  width: { xs: '150px', md: '180px' },
                  height: { xs: '150px', md: '180px' },
                  borderRadius: '50%',
                  objectFit: 'cover',
                  display: 'block',
                  mx: 'auto',
                  mb: 3,
                  border: '3px solid #f4f4f4',
                  boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                }}
              />
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>Mr. Prithiv</Typography>
              <Typography sx={{ color: '#cc0000', fontSize: '14px', fontWeight: 'bold' }}>FOUNDER & CEO</Typography>
            </Grid>
            <Grid item xs={12} md={9}>
              <Typography sx={{ fontSize: '16px', color: '#555', mb: 3 }}>
                A social-impact-driven entrepreneur dedicated to women’s health, CSR implementation, and sustainable technology solutions. Driven by the belief that business can create social good, the founder focuses on building scalable fem tech models aligned with corporate CSR objectives and community needs.
              </Typography>

              <Box sx={{ position: 'relative', pl: 3 }}>
                <Typography sx={{
                  fontSize: '20px',
                  fontStyle: 'italic',
                  color: '#003366',
                  fontFamily: 'Georgia, serif',
                  mb: 1
                }}>
                  "Menstrual hygiene is not just a health issue — it is a matter of dignity, equality, and opportunity. Our mission is to transform access through technology and create safe spaces for women and girls across India."
                </Typography>
                <Typography variant="caption" sx={{ color: '#999', fontWeight: 'bold', textTransform: 'uppercase' }}>
                  — Founder’s Message
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

      </Container>
    </Box>
  );
}

export default About;
